import { type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import Tabs, { Tab, TabList } from '@atlaskit/tabs';
import type { Avatars } from '@trello/business-logic/member';
import type { PIIString } from '@trello/privacy';
import { navigate } from '@trello/router/navigate';
import { getTestId, type ProfileTestIds } from '@trello/test-ids';
import {
  getMemberAccountUrl,
  getMemberActivityUrl,
  getMemberCardsUrl,
  getMemberProfileUrl,
} from '@trello/urls';

import { useDocumentTitle } from 'app/src/components/DocumentTitle';
import { MemberHomeHeader } from './MemberHomeHeader';
import { usePageTitle, type PageTitle } from './usePageTitle';

import * as styles from './MemberHeaderParent.module.less';

interface MemberHeaderParentProps {
  id: string;
  initials: PIIString;
  username: PIIString;
  name?: PIIString;
  avatars?: Avatars;
  isMemberMe: boolean;
  selected: PageTitle;
}

export const MemberHeaderParent: FunctionComponent<MemberHeaderParentProps> = ({
  id,
  initials,
  username,
  name,
  avatars,
  isMemberMe,
  selected,
}) => {
  const getSelectedIndex = () => {
    // Get the index of the selected tab
    const tabs = isMemberMe
      ? ['Profile', 'Activity', 'Cards', 'Settings']
      : ['Activity', 'Cards'];

    return tabs.findIndex((tab) => tab === selected);
  };

  const pageTitle = usePageTitle({
    title: selected,
    username,
    name,
    isMemberMe,
  });
  useDocumentTitle(pageTitle);

  return (
    <>
      <div>
        <div className={styles.headerWrapperMember}>
          <MemberHomeHeader
            id={id}
            initials={initials}
            username={username}
            name={name}
            avatars={avatars}
          />
        </div>
      </div>
      <div className={styles.tabWrapper}>
        <Tabs
          onChange={(index) => {
            const routes = isMemberMe
              ? [
                  getMemberProfileUrl(username),
                  getMemberActivityUrl(username),
                  getMemberCardsUrl(username),
                  getMemberAccountUrl(username),
                ]
              : [getMemberActivityUrl(username), getMemberCardsUrl(username)];
            navigate(routes[index], { trigger: true });
          }}
          selected={getSelectedIndex()}
          id="default"
        >
          <TabList>
            {isMemberMe && (
              <Tab testId={getTestId<ProfileTestIds>('profile-username-tab')}>
                <strong>
                  <FormattedMessage
                    id="templates.account_menu.profile-and-visibility"
                    defaultMessage="Profile and visibility"
                    description="Tab label for profile and visibility section"
                  />
                </strong>
              </Tab>
            )}
            <Tab testId={getTestId<ProfileTestIds>('profile-activity-tab')}>
              <strong>
                <FormattedMessage
                  id="templates.account_menu.activity"
                  defaultMessage="Activity"
                  description="Tab label for activity section"
                />
              </strong>
            </Tab>
            <Tab testId={getTestId<ProfileTestIds>('profile-cards-tab')}>
              <strong>
                <FormattedMessage
                  id="templates.account_menu.cards"
                  defaultMessage="Cards"
                  description="Tab label for cards section"
                />
              </strong>
            </Tab>
            {isMemberMe && (
              <Tab testId={getTestId<ProfileTestIds>('profile-settings-tab')}>
                <strong>
                  <FormattedMessage
                    id="templates.account_menu.settings"
                    defaultMessage="Settings"
                    description="Tab label for settings section"
                  />
                </strong>
              </Tab>
            )}
          </TabList>
        </Tabs>
      </div>
    </>
  );
};
